'use client';

import { type CSSProperties, type FC, type ReactNode, useMemo } from 'react';

import clsx from 'clsx';
import { Pie, PieChart as RePieChart, Tooltip as ReTooltip } from 'recharts';

import { Legend, type LegendProps } from '@/components/common/charts/Legend';
import { PieSector } from '@/components/common/charts/PieChart/components/PieSector/PieSector';
import { ResponsiveContainer } from '@/components/common/charts/ResponsiveContainer';
import { Tooltip } from '@/components/common/charts/Tooltip';
import { Flex } from '@/components/common/Flex';
import { useDeviceProperties } from '@/hooks/use-device-properties';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';
import { getCssSizeValue } from '@/utils/css';
import { getColorCssValue } from '@/utils/theme';

import styles from './PieChart.module.scss';

export type Sector = {
  id: string;
  name: string;
  value: number;
  color: RegularColor;
  legendLabel?: string;
};

export type PieChartProps = WithClassName & {
  sectors: Sector[];
  innerRadiusProportion?: number;
  startAngle?: number;
  endAngle?: number;
  paddingAngle?: number;
  withLegend?: boolean;
  legendPosition?: 'right' | 'bottom';
  legendDirection?: LegendProps['direction'];
  width?: number | string;
  height?: number | string;
  children?: ReactNode;
};

const FAKE_SECTOR_ID = 'pie:fake';

export const PieChart: FC<PieChartProps> = (props) => {
  const {
    className,
    sectors,
    innerRadiusProportion = 0.65,
    startAngle = 90,
    endAngle = startAngle - 360,
    paddingAngle = 3,
    withLegend,
    legendDirection = 'column',
    width,
    height,
    children,
  } = props;

  const { isMobile } = useDeviceProperties();
  const centerNodeSize = `calc(50% * ${innerRadiusProportion * Math.SQRT2})`;

  const renderedSectors = useMemo<Sector[]>(() => {
    for (const sector of sectors) {
      if (sector.value) {
        return sectors;
      }
    }

    return [
      ...sectors,
      {
        id: FAKE_SECTOR_ID,
        name: '',
        value: 1,
        color: 'white-50p',
      },
    ];
  }, [sectors]);

  const colorsStyle = useMemo(() => {
    const style: CSSProperties = {};

    sectors.forEach(({ id, color }) => {
      style[`--sector-${id}-color`] = getColorCssValue(color);
    });

    return style;
  }, [sectors]);

  return (
    <Flex
      className={clsx(styles.root, className)}
      gap={9}
      alignItems="center"
      justifyContent="spaceBetween"
      style={{
        '--pie-chart-width': getCssSizeValue(width),
        '--pie-chart-height': getCssSizeValue(height),
      }}
    >
      <div className={styles.chartContainer}>
        {children && (
          <Flex
            className={styles.centerNode}
            style={{ width: centerNodeSize, height: centerNodeSize }}
            direction="column"
            alignItems="center"
            justifyContent="center"
          >
            {children}
          </Flex>
        )}

        <ResponsiveContainer className={styles.chart}>
          <RePieChart>
            <Pie
              data={renderedSectors}
              dataKey="value"
              outerRadius="100%"
              innerRadius={`${100 * innerRadiusProportion}%`}
              startAngle={startAngle}
              endAngle={endAngle}
              paddingAngle={paddingAngle}
              shape={PieSector}
            />

            <ReTooltip
              shared={false}
              cursor={false}
              content={Tooltip}
              isAnimationActive={false}
              wrapperStyle={colorsStyle}
            />
          </RePieChart>
        </ResponsiveContainer>
      </div>

      {withLegend && (
        <Legend
          className={styles.legend}
          alignItems={isMobile ? 'center' : 'flexEnd'}
          datasets={sectors.map(({ id, name, color, legendLabel }) => ({
            id,
            name: legendLabel ?? name,
            value: legendLabel ? name : undefined,
            color,
          }))}
          direction={legendDirection}
        />
      )}
    </Flex>
  );
};
